import React from "react";
import { makeStyles } from "tss-react/mui";
import ZoneSvgPoint from "./ZoneSvgPoint";
import Tooltip from "@mui/material/Tooltip";
import { ZoneSubTypography, ZoneTypography } from "./ZoneTypography";
import { useTranslation } from "react-i18next";
import Gate from "../../../interfaces/robot/rooms/gate";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { getSvgCoordinatesFromRosPoint } from "../../../interfaces/robot/rooms/point";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { useSharedActions, useSharedDispatch } from "../../../store/SharedProvider";

const useStyles = makeStyles()((theme) => ({
  gateLine: {
    stroke: GATE_COLOR,
    pointerEvents: "none",
  },
  gateLineOuter: {
    strokeLinejoin: "round",
    stroke: GATE_COLOR,
    strokeOpacity: 0.3,
    pointerEvents: "visible",
  },
  gateLineHover: {
    "&:hover": {
      strokeOpacity: 0.7,
    },
  },
  showHover: {
    strokeOpacity: 0.7,
  },
  notSelectable: {
    pointerEvents: "none",
    opacity: 0.4,
  },
}));

export const GATE_COLOR = "rgb(165,42,42)";

const GatePosts: React.FC<{
  gate: Gate;
  scale: number;
  roomData: RoomData;
  isHovering: boolean;
  mouseState: ZoneControllerMouseState;
  notSelectable?: boolean;
}> = ({ gate, scale, roomData, isHovering, mouseState, notSelectable = false }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const {
      deleteGateById,
      setSelectedGate
    } = useSharedActions();

  const gateClickHandler = () => {
    if (notSelectable) return;
    if (mouseState === ZoneControllerMouseState.DeleteZone) {
      dispatch(deleteGateById(gate.id));
    } else {
      dispatch(setSelectedGate(gate.id));
    }
  };

  const enableHover =
    !notSelectable &&
    (mouseState === ZoneControllerMouseState.DeleteZone || mouseState === ZoneControllerMouseState.Default);

  const gateAPostSvgCoordinates = getSvgCoordinatesFromRosPoint(gate.aPost, roomData);
  const gateBPostSvgCoordinates = getSvgCoordinatesFromRosPoint(gate.bPost, roomData);

  return (
    <React.Fragment>
      <ZoneSvgPoint
        point={{
          x: gateAPostSvgCoordinates!.x,
          y: gateAPostSvgCoordinates!.y,
        }}
        id={gate.aPost.id}
        scale={scale}
        type={ZonePointType.GatePoint}
        enlargingOnHoverEnabled={!notSelectable}
        midPointInsertAtIndex={null}
        notSelectable={notSelectable}
      />
      <ZoneSvgPoint
        point={{
          x: gateBPostSvgCoordinates!.x,
          y: gateBPostSvgCoordinates!.y,
        }}
        id={gate.bPost.id}
        scale={scale}
        type={ZonePointType.GatePoint}
        enlargingOnHoverEnabled={!notSelectable}
        midPointInsertAtIndex={null}
        notSelectable={notSelectable}
      />
      <line
        x1={gateAPostSvgCoordinates!.x}
        y1={gateAPostSvgCoordinates!.y}
        x2={gateBPostSvgCoordinates!.x}
        y2={gateBPostSvgCoordinates!.y}
        className={cx(classes.gateLine, {[classes.notSelectable]: notSelectable})}
        strokeWidth={5 / scale}
        data-testid={"gateLineTestId"}
      />
      <Tooltip
        followCursor={true}
        title={
          enableHover ? (
            <React.Fragment>
              <ZoneTypography variant="body2" noWrap>
                {gate.name}
              </ZoneTypography>
              <ZoneSubTypography>
                {mouseState === ZoneControllerMouseState.DeleteZone
                  ? t("tooltip-delete-gate")
                  : t("tooltip-select-gate")}
              </ZoneSubTypography>
            </React.Fragment>
          ) : undefined
        }
      >
        <polygon
          points={`${gateAPostSvgCoordinates!.x},${gateAPostSvgCoordinates!.y},${gateBPostSvgCoordinates!.x},${
            gateBPostSvgCoordinates!.y
          }`}
          className={cx(classes.gateLineOuter, {
            [classes.gateLineHover]: enableHover,
            [classes.showHover]: isHovering,
          })}
          strokeWidth={12}
          onClick={gateClickHandler}
          style={{ pointerEvents: !enableHover ? "none" : undefined }}
        />
      </Tooltip>
    </React.Fragment>
  );
};

export default GatePosts;
